import {
  createContext,
  useContext,
  useState,
  useEffect,
  type FC,
  type PropsWithChildren,
} from 'react';
import type { CongressBase, User } from '@domain/index';
import { useRouter } from 'next/router';
import { useMe } from '@data/useMe';
import MainLayout from '@layouts/main';

interface AppContextValue {
  user: User;
  chosenCongress?: CongressBase;
  setChosenCongress: (congress: CongressBase | undefined) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider: FC<PropsWithChildren> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [chosenCongress, setChosenCongress] = useState<
    CongressBase | undefined
  >(undefined);
  const { error, me } = useMe();

  if (error != null) throw error;

  const setAndStoreChosenCongress = (
    congress: CongressBase | undefined
  ): void => {
    setChosenCongress(congress);
    if (congress != null) {
      localStorage.setItem('chosenCongressId', congress.id);
    } else {
      localStorage.removeItem('chosenCongressId');
    }
  };

  useEffect(() => {
    if (me != null) {
      if (chosenCongress == null || !me.congresses.includes(chosenCongress)) {
        if (me.congresses.length === 1) {
          setAndStoreChosenCongress(me.congresses[0]);
          return;
        }

        const storedCongressId = localStorage.getItem('chosenCongressId');
        if (storedCongressId != null) {
          const foundCongress = me.congresses.find(
            c => c.id === storedCongressId
          );
          if (foundCongress != null) {
            setAndStoreChosenCongress(foundCongress);
            return;
          }
        }

        setAndStoreChosenCongress(undefined);
      }
      setReady(true);
    }
  }, [me, chosenCongress]);

  if (!ready) {
    return (
      <MainLayout
        alignment="top"
        panelled={false}
        restrictWidth={true}
        showSpinner
        noAuth
      />
    );
  }

  const value: AppContextValue = {
    user: me as User,
    chosenCongress,
    setChosenCongress: setAndStoreChosenCongress,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

interface Options {
  redirect?: boolean;
}

export const useAppContext = (options: Options = {}): AppContextValue => {
  const { redirect = false } = options;

  const context = useContext(AppContext);
  const router = useRouter();

  if (context == null) {
    throw new Error('useAppContext must be used within an AppProvider');
  }

  const { chosenCongress } = context;

  useEffect(() => {
    if (redirect && chosenCongress == null) {
      router.push('/choose-congress');
    }
  }, [redirect, chosenCongress, router]);

  return context;
};
